import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, Cpu, Database, Clock, AlertTriangle, MemoryStick,
  RefreshCw, MessageCircle, Server, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" }); } catch { return iso; }
}

function KpiBlock({ icon, label, value, tone = "slate", testId, hint }) {
  const tones = {
    slate:   "border-slate-800 bg-slate-950/60",
    emerald: "border-emerald-900 bg-emerald-950/40",
    amber:   "border-amber-900 bg-amber-950/40",
    rose:    "border-rose-900 bg-rose-950/40",
    blue:    "border-blue-900 bg-blue-950/40",
  };
  return (
    <div data-testid={testId} className={`border rounded-sm p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-lg font-bold text-white font-mono tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function SystemHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedErrors, setExpandedErrors] = useState(false);
  const [expandedExc, setExpandedExc] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/system/health");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(load, 15 * 1000);
    return () => clearInterval(iv);
  }, [autoRefresh, load]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 p-8">
        <RefreshCw className="w-4 h-4 animate-spin" /> Sistem sağlığı yükleniyor…
      </div>
    );
  }
  if (!data) return null;

  const errs5xx = data.last_hour?.http_errors_5xx || {};
  const exc = data.last_hour?.exception_count || 0;
  const anyIssue = (exc + Object.values(errs5xx).reduce((a, b) => a + b, 0)) > 0;
  const ram = data.system?.ram || {};
  const proc = data.process || {};

  return (
    <div className="space-y-4" data-testid="system-health-tab">
      {/* Header + control */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">Sunucu Sağlığı ve Tanı</div>
          <div className="text-sm text-slate-300 mt-1">
            Cloudflare 520/521/524 tarzı hataların kök nedenini bu panelden takip edin.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={`rounded-sm ${anyIssue ? "bg-rose-950 text-rose-300 border-rose-800" : "bg-emerald-950 text-emerald-300 border-emerald-800"}`}
            data-testid="health-overall-badge"
          >
            {anyIssue ? "DİKKAT" : "SAĞLIKLI"}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 rounded-sm"
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="health-autorefresh-toggle"
          >
            {autoRefresh ? "Oto yenile: AÇIK" : "Oto yenile: KAPALI"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 rounded-sm"
            onClick={load}
            disabled={loading}
            data-testid="health-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
        </div>
      </div>

      {/* Row 1: Core process */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiBlock
          icon={<Server className="w-3.5 h-3.5" />}
          label="Backend"
          value={data.database?.status === "ok" ? "AYAKTA" : "SORUN"}
          tone={data.database?.status === "ok" ? "emerald" : "rose"}
          testId="kpi-backend-status"
          hint={`PID ${proc.pid}`}
        />
        <KpiBlock
          icon={<Clock className="w-3.5 h-3.5" />}
          label="Çalışma Süresi"
          value={data.boot?.uptime_human || "—"}
          tone="blue"
          testId="kpi-uptime"
          hint={`Başlangıç: ${fmtDate(data.boot?.started_at)}`}
        />
        <KpiBlock
          icon={<Database className="w-3.5 h-3.5" />}
          label="MongoDB"
          value={data.database?.status === "ok" ? "BAĞLI" : "HATA"}
          tone={data.database?.status === "ok" ? "emerald" : "rose"}
          testId="kpi-db-status"
          hint={data.database?.ping_ms != null ? `Ping ${data.database.ping_ms} ms` : "—"}
        />
        <KpiBlock
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Aktif Görevler"
          value={data.event_loop_active_tasks ?? "—"}
          tone="slate"
          testId="kpi-loop-tasks"
          hint="Event-loop task sayısı"
        />
      </div>

      {/* Row 2: CPU / RAM */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiBlock
          icon={<Cpu className="w-3.5 h-3.5" />}
          label="Backend CPU"
          value={proc.cpu_percent != null ? `${proc.cpu_percent.toFixed(1)}%` : "—"}
          tone={(proc.cpu_percent || 0) > 80 ? "rose" : (proc.cpu_percent || 0) > 50 ? "amber" : "emerald"}
          testId="kpi-proc-cpu"
        />
        <KpiBlock
          icon={<MemoryStick className="w-3.5 h-3.5" />}
          label="Backend RAM"
          value={proc.rss_mb != null ? `${proc.rss_mb.toFixed(0)} MB` : "—"}
          tone={(proc.rss_mb || 0) > 1500 ? "rose" : (proc.rss_mb || 0) > 800 ? "amber" : "emerald"}
          testId="kpi-proc-rss"
          hint={`VMS ${proc.vms_mb?.toFixed(0) || "—"} MB`}
        />
        <KpiBlock
          icon={<Cpu className="w-3.5 h-3.5" />}
          label="Sistem CPU"
          value={data.system?.cpu_percent != null ? `${data.system.cpu_percent.toFixed(1)}%` : "—"}
          tone={(data.system?.cpu_percent || 0) > 80 ? "rose" : "slate"}
          testId="kpi-sys-cpu"
          hint={`load ${data.system?.load_avg?.[0]?.toFixed(2) || "—"} · ${data.system?.load_avg?.[1]?.toFixed(2) || "—"} · ${data.system?.load_avg?.[2]?.toFixed(2) || "—"}`}
        />
        <KpiBlock
          icon={<MemoryStick className="w-3.5 h-3.5" />}
          label="Sistem RAM"
          value={ram.percent != null ? `${ram.percent}%` : "—"}
          tone={(ram.percent || 0) > 90 ? "rose" : (ram.percent || 0) > 75 ? "amber" : "emerald"}
          testId="kpi-sys-ram"
          hint={`Boş ${ram.available_mb?.toFixed(0) || "—"} / ${ram.total_mb?.toFixed(0) || "—"} MB`}
        />
      </div>

      {/* Row 3: 5xx errors */}
      <Card className="bg-slate-950/40 border-slate-800 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Son 1 Saat — HTTP 5xx Sayaçları
          </div>
          <div className="text-[10px] text-slate-500">Cloudflare 520/521/524 → 500/502/504'e karşılık gelir</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["500", "502", "503", "504"].map((code) => (
            <KpiBlock
              key={code}
              icon={<AlertTriangle className="w-3.5 h-3.5" />}
              label={`${code} sayısı`}
              value={errs5xx[code] ?? 0}
              tone={(errs5xx[code] || 0) > 0 ? "rose" : "emerald"}
              testId={`kpi-http-${code}`}
            />
          ))}
        </div>
      </Card>

      {/* Row 4: WhatsApp + PDKS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-slate-950/40 border-slate-800 p-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Webhook
          </div>
          <div className="mt-2 text-sm text-slate-300">
            Son erişim: <span className="font-mono text-white" data-testid="health-wa-last">{fmtDate(data.whatsapp_webhook?.last_received_at)}</span>
          </div>
          <div className="mt-1 text-xs text-slate-400">
            Son sonuç: <Badge className="bg-slate-900 border-slate-700 text-slate-200 rounded-sm text-[10px]" data-testid="health-wa-outcome">{data.whatsapp_webhook?.last_outcome || "—"}</Badge>
          </div>
        </Card>
        <Card className="bg-slate-950/40 border-slate-800 p-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> PDKS Terminal
          </div>
          <div className="mt-2 text-sm text-slate-300">
            Son giriş/çıkış: <span className="font-mono text-white" data-testid="health-pdks-last">{fmtDate(data.pdks_terminal?.last_stamp_at)}</span>
          </div>
        </Card>
      </div>

      {/* Collection sizes */}
      <Card className="bg-slate-950/40 border-slate-800 p-3">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Koleksiyon Boyutları</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(data.collections || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border border-slate-800 rounded-sm px-2 py-1" data-testid={`coll-${k}`}>
              <span className="text-slate-400 font-mono">{k}</span>
              <span className="font-mono text-slate-200 tabular-nums">{v}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent exceptions */}
      <Card className="bg-slate-950/40 border-slate-800 p-3">
        <button
          onClick={() => setExpandedExc((v) => !v)}
          className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 hover:text-slate-200"
          data-testid="health-toggle-exceptions"
        >
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Son Backend Exception'ları ({data.last_hour?.exception_count || 0})
          </span>
          {expandedExc ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {expandedExc && (
          <div className="mt-2 space-y-1 text-xs max-h-64 overflow-y-auto">
            {(data.last_hour?.last_10_exceptions || []).length === 0 && (
              <div className="text-slate-500">Son 1 saatte kayıtlı exception yok.</div>
            )}
            {(data.last_hour?.last_10_exceptions || []).slice().reverse().map((e, i) => (
              <div key={i} className="border border-rose-950 bg-rose-950/20 rounded-sm px-2 py-1 font-mono">
                <div className="text-rose-300">
                  <span className="text-slate-400">{fmtDate(e.at)}</span> · <span className="uppercase">{e.method}</span> {e.path} · <b>{e.type}</b>
                </div>
                <div className="text-slate-300 text-[10px] mt-0.5 break-all">{e.message}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent 5xx */}
      <Card className="bg-slate-950/40 border-slate-800 p-3">
        <button
          onClick={() => setExpandedErrors((v) => !v)}
          className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 hover:text-slate-200"
          data-testid="health-toggle-http-errors"
        >
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Son 5xx HTTP Hataları ({data.last_hour?.http_error_count || 0})
          </span>
          {expandedErrors ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {expandedErrors && (
          <div className="mt-2 space-y-1 text-xs max-h-64 overflow-y-auto">
            {(data.last_hour?.last_10_http_errors || []).length === 0 && (
              <div className="text-slate-500">Son 1 saatte 5xx hatası yok.</div>
            )}
            {(data.last_hour?.last_10_http_errors || []).slice().reverse().map((e, i) => (
              <div key={i} className="border border-amber-950 bg-amber-950/20 rounded-sm px-2 py-1 font-mono">
                <span className="text-amber-300">{fmtDate(e.at)}</span> · <b>{e.status}</b> <span className="uppercase">{e.method}</span> {e.path} · {e.duration_ms} ms
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Cloudflare hints */}
      <Card className="bg-slate-950/40 border-slate-800 p-3">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Cloudflare 520 Kök Neden Rehberi</div>
        <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4">
          {(data.cloudflare_hint?.typical_520_causes || []).map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
